import React from 'react';
import { Field } from 'react-final-form';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Add from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import { MultiselectField, SelectField } from '../../FinalForm';
import { DomainSortButton } from '../components/DomainSortButton';
import { FieldArray } from 'react-final-form-arrays';

export function DomainAffiliate({ domains, affiliateNames, isLoading, domainSortDirection, onToggleDomainSortDirection }) {
  const domainTags = domains.filter((item) => item.name.startsWith('#'));
  const domainNames = domains.filter((item) => !item.name.startsWith('#'));
  const sortedDomains = [...domainTags, ...domainNames];
  const affiliateTags = affiliateNames.filter((item) => item.name.startsWith('#'));
  const affiliates = affiliateNames.filter((item) => !item.name.startsWith('#'));
  const sortedAffiliates = [...affiliateTags, ...affiliates];

  return (
    <Grid container spacing={2} sx={{ mt: 1, alignItems: 'start' }}>
      <Grid size={{ xs: 12, lg: 3 }}>
        <Stack direction='row' spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant='h5'>Domain-specific Affiliates</Typography>
          {onToggleDomainSortDirection && (
            <DomainSortButton direction={domainSortDirection} onToggle={onToggleDomainSortDirection} />
          )}
        </Stack>
      </Grid>
      <Grid size={{ xs: 12, lg: 10 }}>
        <FieldArray name='offer.domainAffiliateConfiguration.domainAffiliateEntries'>
          {({ fields }) => {
            return (
              <>
                {fields.map((name, index) => (
                  <div key={name}>
                    <Card>
                      <CardHeader
                        slotProps={{ title: { component: 'div' } }}
                        title={
                          <Tooltip title='Remove Domain-specific Affiliates'>
                            <IconButton
                              color='error'
                              type='button'
                              aria-label='Remove Domain-specific Affiliates'
                              onClick={() => fields.remove(index)}
                            >
                              <Close fontSize='small' />
                            </IconButton>
                          </Tooltip>
                        }
                      />
                      <CardContent>
                        <Grid container spacing={2} sx={{ alignItems: 'start' }}>
                          <Grid size='grow'>
                            <FormLabel component='span' sx={{ display: 'block', mb: 0.5 }}>
                              Domains
                            </FormLabel>
                            <Field name={`${name}.domains`}>
                              {({ input, meta }) => (
                                <>
                                  <MultiselectField
                                    {...input}
                                    data={sortedDomains}
                                    textField={(item) =>
                                      item.name && item.name.startsWith('#') ? `${item.id}` : `${item.id}: ${item.name}`
                                    }
                                    dataKey='name'
                                    placeholder={'Select Domain'}
                                    busy={isLoading}
                                    groupBy={(item) => (item.name.startsWith('#') ? 0 : 1)}
                                    renderListGroup={({ group }) =>
                                      group === 0 ? <span>Tags</span> : <span>Domains</span>
                                    }
                                  />
                                </>
                              )}
                            </Field>
                          </Grid>
                        </Grid>
                        {fields.value[index].domains?.length !== 0 && (
                          <Grid container spacing={2} sx={{ pt: 2, alignItems: 'start' }}>
                            <Grid size={{ xs: 12, lg: 3 }}>
                              <FormLabel component='span' sx={{ display: 'block', mb: 0.5 }}>
                                Include/Exclude
                              </FormLabel>
                              <SelectField
                                name={`${name}.includeExcludeAction`}
                                options={[
                                  {
                                    value: null,
                                    option: 'None',
                                  },
                                  {
                                    value: 'include',
                                    option: 'Include',
                                  },
                                  {
                                    value: 'exclude',
                                    option: 'Exclude',
                                  },
                                ]}
                              />
                            </Grid>
                            <Grid size='grow'>
                              <FormLabel component='span' sx={{ display: 'block', mb: 0.5 }}>
                                Affiliate
                              </FormLabel>
                              <Field name={`${name}.affiliates`}>
                                {({ input, meta }) => (
                                  <>
                                    <MultiselectField
                                      {...input}
                                      data={sortedAffiliates}
                                      textField={(item) =>
                                        item.name && item.name.startsWith('#')
                                          ? `${item.id}`
                                          : `${item.id}: ${item.name}`
                                      }
                                      dataKey='name'
                                      placeholder={'Select affiliate'}
                                      busy={isLoading}
                                      groupBy={(item) => (item.name.startsWith('#') ? 0 : 1)}
                                      renderListGroup={({ group }) =>
                                        group === 0 ? <span>Tags</span> : <span>Affiliates</span>
                                      }
                                    />
                                  </>
                                )}
                              </Field>
                            </Grid>
                          </Grid>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                ))}
                <Grid container spacing={2} sx={{ alignItems: 'start' }}>
                  <Grid size={{ xs: 12, lg: 2 }}>
                    <Tooltip title='Add Domain-specific Affiliates'>
                      <IconButton
                        color='success'
                        aria-label='Add Domain-specific Affiliates'
                        onClick={() => fields.push({ domains: [], affiliates: [], includeExcludeAction: null })}
                      >
                        <Add />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </Grid>
              </>
            );
          }}
        </FieldArray>
      </Grid>
    </Grid>
  );
}
